<template>
  <div class="d-flex flex-wrap justify-between align-center" style="gap: 10px">
    <h1>Client Users</h1>

    <div
      class="d-flex justify-end"
      :class="$vuetify.display.mdAndDown ? 'flex-wrap' : 'ga-3'"
      style="gap: 0px; flex: 1"
    >
      <div
        :style="$vuetify.display.mdAndDown ? 'max-width:100%' : 'max-width:300px'"
        style="width: 100%; text-align: right"
      >
        <v-btn
          data-testid="add-client-user-btn"
          variant="text"
          size="small"
          prepend-icon="mdi-plus"
          class="text-grey-darken-2"
          @click="goToCreateUser"
        >
          Add New Client User
        </v-btn>
      </div>
    </div>
  </div>

  <p class="my-6 text-body-2 text-grey-darken-1">
    Manage client users. Use the actions menu to update roles, activate/deactivate, or delete.
  </p>

  <v-card>
    <v-toolbar color="primary" density="comfortable" flat>
      <!-- Filter Menu Button -->
      <v-menu offset-y v-model="filterMenuOpen" :close-on-content-click="false">
        <template v-slot:activator="{ props }">
          <v-btn
            data-testid="filter-menu-btn"
            v-bind="props"
            variant="text"
            class="text-white ml-2"
            prepend-icon="mdi-filter-variant"
          >
            Filter Roles
            <v-badge
              v-if="selectedRoles.length < 3"
              :content="selectedRoles.length"
              color="error"
              inline
              class="ml-2"
            />
          </v-btn>
        </template>
        <v-list density="compact">
          <v-list-item>
            <v-checkbox
              data-testid="filter-user"
              v-model="tempSelectedRoles"
              value="user"
              density="compact"
              hide-details
              :label="`User (${roleCounts.user})`"
            />
          </v-list-item>
          <v-list-item>
            <v-checkbox
              data-testid="filter-manager"
              v-model="tempSelectedRoles"
              value="manager"
              density="compact"
              hide-details
              :label="`Manager (${roleCounts.manager})`"
            />
          </v-list-item>
          <v-list-item>
            <v-checkbox
              data-testid="filter-admin"
              v-model="tempSelectedRoles"
              value="admin"
              density="compact"
              hide-details
              :label="`Admin (${roleCounts.admin})`"
            />
          </v-list-item>
          <v-divider class="my-2"></v-divider>
          <v-list-item>
            <div class="d-flex ga-2" style="width: 100%">
              <v-btn
                data-testid="filter-check-all-btn"
                size="small"
                variant="text"
                @click="checkAllFilters"
              >
                Check All
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                data-testid="filter-apply-btn"
                size="small"
                color="primary"
                variant="flat"
                @click="applyFilters"
              >
                Apply
              </v-btn>
            </div>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-spacer></v-spacer>
      <!-- Desktop search -->
      <v-text-field
        v-if="!$vuetify.display.mdAndDown"
        data-testid="user-search-input"
        ref="searchRef"
        v-model="searchQuery"
        placeholder="Search users…"
        clearable
        @click:clear="searchQuery = ''"
        prepend-inner-icon="mdi-magnify"
        hide-details
        density="compact"
        variant="solo"
        class="search-in-bar mr-2"
        style="max-width: 380px"
      ></v-text-field>
    </v-toolbar>
    <!-- Mobile search: full width row below tabs -->
    <div class="px-2 pt-2 mb-2" v-if="$vuetify.display.mdAndDown">
      <v-text-field
        data-testid="user-search-input"
        ref="searchRef"
        v-model="searchQuery"
        placeholder="Search users…"
        clearable
        @click:clear="searchQuery = ''"
        prepend-inner-icon="mdi-magnify"
        hide-details
        density="comfortable"
        variant="solo"
        style="width: 100%; max-width: 100%"
      ></v-text-field>
    </div>
  </v-card>

  <!-- Users/Admins Table -->
  <v-data-table
    data-testid="client-users-table"
    :items-per-page="50"
    :headers="headers"
    fixed-header
    style="max-width: 100%; width: 100%; overflow-x: auto"
    :items="filteredDisplayedUsers"
    :loading="isLoading"
    class="elevation-1 sticky-header striped-rows even-cols"
    :style="{ '--even-col-count': headers.length }"
    item-value="username"
    loading-text="Loading users..."
  >
    <template #item.username="{ item }">
      <div class="d-flex align-center" style="width: 100%">
        <span>{{ item.username }}</span>
        <v-tooltip activator="parent" location="top">{{ item?.email || '—' }}</v-tooltip>
      </div>
    </template>

    <template #item.accountJuid="{ item }">
      <div class="d-flex align-center justify-center" style="width: 100%">
        <router-link
          v-if="item?.accountJuid"
          :to="`/account-admin/${item.accountJuid}#info`"
          class="text-primary text-decoration-none"
        >
          {{ item.accountFriendlyName || item.accountJuid }}
        </router-link>
        <v-tooltip v-if="item?.accountJuid" activator="parent" location="top">
          {{ item?.accountJuid || '—' }}
        </v-tooltip>
        <span v-else>—</span>
      </div>
    </template>

    <template #item.status="{ item }">
      <div class="d-flex align-center justify-center" style="width: 100%">
        <v-chip
          size="x-small"
          :color="item?.status === 'Verified' ? 'green' : 'red'"
          variant="tonal"
        >
          {{ item?.status }}
        </v-chip>
      </div>
    </template>

    <template #item.actions="{ item }">
      <div class="d-flex align-center">
        <!-- 3-dot menu -->
        <v-menu offset-y>
          <template v-slot:activator="{ props }">
            <v-btn
              icon
              variant="text"
              size="x-small"
              class="subtle-action mr-2"
              color="grey-darken-1"
              v-bind="props"
              @click.stop
            >
              <v-icon size="20">mdi-dots-horizontal</v-icon>
            </v-btn>
          </template>

          <v-list dense>
            <!-- Resend Verification Email (only for unverified users) -->
            <v-list-item
              v-if="item.status === 'Unverified'"
              data-testid="resend-verification-btn"
              @click.stop="resendVerification(item.username)"
              prepend-icon="mdi-email-sync"
            >
              Resend Verification Email
            </v-list-item>

            <!-- Update User Role -->
            <v-list-item
              data-testid="update-role-btn"
              @click.stop="openRoleDialog(item)"
              prepend-icon="mdi-account-edit"
            >
              Update User Role
            </v-list-item>

            <!-- Toggle Active/Inactive -->
            <v-list-item
              data-testid="toggle-status-btn"
              @click.stop="promptToggle(item)"
              :prepend-icon="item.enabled ? 'mdi-toggle-switch' : 'mdi-toggle-switch-off'"
            >
              {{ item.enabled ? 'Activated' : 'Deactivated' }}
            </v-list-item>

            <!-- Delete User -->
            <v-list-item
              data-testid="delete-user-btn"
              @click.stop="promptDelete(item.username)"
              prepend-icon="mdi-delete"
            >
              Delete User
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </template>
  </v-data-table>

  <!-- Delete Confirmation Dialog -->
  <v-dialog v-model="deleteDialog" max-width="400px" data-testid="delete-dialog">
    <v-card>
      <v-card-title style="font-weight: 600" class="headline">Confirm Delete</v-card-title>
      <v-card-text>
        Are you sure you want to permanently delete user
        <strong>{{ userToDelete }}</strong>
        ?
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn data-testid="delete-dialog-cancel" text @click="deleteDialog = false">Cancel</v-btn>
        <v-btn
          data-testid="delete-dialog-confirm"
          color="red"
          text
          @click="selectedRole == 'clients' ? confirmDeleteClients() : confirmDelete()"
        >
          Delete
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-dialog v-model="toggleDialog" max-width="400px" data-testid="toggle-dialog">
    <v-card>
      <v-card-title class="headline">
        {{ toggleTarget.enabled ? 'Deactivate' : 'Activate' }} "{{ toggleTarget.username }}"?
      </v-card-title>
      <v-card-text>
        Are you sure you want to
        <strong>{{ toggleTarget.enabled ? 'deactivate' : 'activate' }}</strong>
        this account?
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn data-testid="toggle-dialog-cancel" text @click="toggleDialog = false">Cancel</v-btn>
        <v-btn
          data-testid="toggle-dialog-confirm"
          color="primary"
          text
          @click="selectedRole == 'clients' ? confirmToggleClient() : confirmToggle()"
        >
          {{ toggleTarget.enabled ? 'Deactivate' : 'Activate' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-dialog v-model="roleDialog" max-width="400px" data-testid="role-dialog">
    <v-card>
      <v-card-title class="headline">Update Role</v-card-title>
      <v-card-text>
        <div>
          Change role for
          <strong>{{ userToUpdateRole?.username }}</strong>
          , Current role is
          <strong>{{ userToUpdateRole?.customRole }}</strong>
          &nbsp;
          <br />
          &nbsp;
          <v-select
            data-testid="role-select"
            v-model="selectedNewRole"
            :items="availableGroupOptions"
            item-title="title"
            item-value="value"
            label="Select new role"
            required
          ></v-select>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn data-testid="role-dialog-cancel" text @click="roleDialog = false">Cancel</v-btn>
        <v-btn data-testid="role-dialog-confirm" color="primary" text @click="confirmUpdateRole">
          Update
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-snackbar v-model="snackbar" color="info" :timeout="3000">
    {{ message }}
    <template v-slot:action="{ attrs }">
      <v-btn color="white" text v-bind="attrs" @click="snackbar = false">Close</v-btn>
    </template>
  </v-snackbar>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import router from '@/router'
import { useRoute } from 'vue-router'
import {
  fetchAllUsers,
  fetchAllClients,
  deleteUser,
  deleteUserClients,
  toggleUserStatusSetActiveNow,
  toggleUserStatusSetDeActiveNow,
  toggleUserStatusSetActiveNowClient,
  toggleUserStatusSetDeActiveNowClient,
  updateRole,
  VerifyAdmin,
  fetchAccountSingle,
  resendVerificationEmailClientByCompany,
} from '@/api/accountsAdmin'
import ProgressLoader from './ProgressLoader.vue'
import { useAccountStore } from '@/stores/accountStore'

const isLoading = ref(true)
const allUsers = ref([])
const allCLients = ref([])
const accountStore = useAccountStore()
const selectedRole = ref('clients')
import { useUserStore } from '@/stores/userStore'
const userStore = useUserStore()
const deleteDialog = ref(false)
const dialogConfirm = ref(false)
const userToDelete = ref(null)

const toggleDialog = ref(false)
const toggleTarget = ref({ username: '', enabled: false })

import Cookies from 'universal-cookie'
const cookies = new Cookies()

const admin = cookies.get('admin')
const myRole = userStore?.role
const message = ref('')
const snackbar = ref(false)
const roleDialog = ref(false)
const userToUpdateRole = ref(null)
const selectedNewRole = ref('')

// Search and Tabs (mirror Users.vue)
const searchQuery = ref('')
const searchRef = ref()
const tab = ref('all')
// Role checkbox state (default all selected)
const selectedRoles = ref(['user', 'manager', 'admin'])
const tempSelectedRoles = ref(['user', 'manager', 'admin'])
const filterMenuOpen = ref(false)

// Map JUID -> friendly name and populate on lists
const accountNameMap = ref({})
async function populateFriendlyNamesForList(listRef) {
  const juids = Array.from(new Set(listRef.value.map((i) => i.accountJuid).filter(Boolean)))
  const missing = juids.filter((j) => !accountNameMap.value[j])
  if (missing.length) {
    await Promise.all(
      missing.map(async (j) => {
        try {
          const data = await fetchAccountSingle(j)
          accountNameMap.value[j] = data?.accountNameFriendly || data?.accountName || j
        } catch (e) {
          accountNameMap.value[j] = j
        }
      })
    )
  }
  listRef.value = listRef.value.map((i) => ({
    ...i,
    accountFriendlyName: accountNameMap.value[i.accountJuid] || i.accountJuid || '',
  }))
}

function openRoleDialog(user) {
  userToUpdateRole.value = user
  selectedNewRole.value = user?.customRoleKey || '' // use raw key as value
  roleDialog.value = true
}

async function confirmUpdateRole() {
  try {
    if (!selectedNewRole.value || !userToUpdateRole.value?.username) return

    const username = userToUpdateRole.value.username
    const newRole = selectedNewRole.value
    
    await updateRole(username, newRole, true)
    
    // Optimistically update the UI without full reload
    const friendlyRoleName = toFriendlyRoleName(newRole)
    
    if (selectedRole.value === 'clients') {
      const clientIndex = allCLients.value.findIndex(u => u.username === username)
      if (clientIndex !== -1) {
        allCLients.value[clientIndex].customRoleKey = newRole
        allCLients.value[clientIndex].customRole = friendlyRoleName
        // Update cache
        accountStore.setClientUsersCache(allCLients.value)
      }
    } else {
      const userIndex = allUsers.value.findIndex(u => u.username === username)
      if (userIndex !== -1) {
        allUsers.value[userIndex].customRoleKey = newRole
        allUsers.value[userIndex].customRole = friendlyRoleName
        // Update cache
        accountStore.setCompanyUsersCache(allUsers.value)
      }
    }
    
    message.value = '✅ Role updated successfully.'
    snackbar.value = true
  } catch (e) {
    console.error('Failed to update role:', e)
    message.value = '❌ Failed to update role.'
    snackbar.value = true
  } finally {
    roleDialog.value = false
  }
}

const hierarchy = ['company_user', 'company_admin', 'company_suadmin', 'company_owner']
const myLevel = hierarchy.indexOf(myRole)
function formatDate(raw) {
  // First normalize your two formats into a real Date()
  let dt
  if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) {
    dt = new Date(raw)
  } else {
    // handle "YYYY-MM-DD hh:mm AM/PM"
    const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
    if (m) {
      let [, y, M, d, h, mm, ampm] = m
      h = parseInt(h, 10)
      if (ampm.toUpperCase() === 'PM' && h !== 12) h += 12
      if (ampm.toUpperCase() === 'AM' && h === 12) h = 0
      dt = new Date(parseInt(y, 10), parseInt(M, 10) - 1, parseInt(d, 10), h, parseInt(mm, 10))
    } else {
      // fallback
      dt = new Date(raw)
    }
  }

  if (isNaN(dt)) return 'Invalid date'
  // now format however you like—here’s US‐style with full date & time:
  return dt.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

// Convert role keys (e.g., "client_manager") to friendly display names (e.g., "Manager")
function toFriendlyRoleName(roleKey) {
  if (!roleKey) return ''
  const key = String(roleKey).replace(/^(company|client)_/, '')
  const map = {
    owner: 'Owner',
    suadmin: 'Super Admin',
    admin: 'Admin',
    manager: 'Manager',
    user: 'User',
  }
  if (map[key]) return map[key]
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

const loadUsers = async () => {
  try {
    if (accountStore.companyUsersCache && accountStore.companyUsersCache.length) {
      allUsers.value = accountStore.companyUsersCache.slice()
      await populateFriendlyNamesForList(allUsers)
      isLoading.value = false
      return
    }
    isLoading.value = true
    const data = await fetchAllUsers()
    allUsers.value = data
      .filter((u) => {
        const roleAttr = u.Attributes.find((a) => a.Name === 'custom:role')?.Value
        const lvl = roleAttr ? hierarchy.indexOf(roleAttr) : 0
        return lvl <= myLevel
      })
      .map((user) => {
        const roleRaw =
          user?.Attributes.find((a) => a.Name === 'custom:role')?.Value || 'client_user'
        return {
          username: user.Username,
          email: user.Attributes.find((attr) => attr.Name === 'email')?.Value || 'N/A',
          userCreateDate: formatDate(user.UserCreateDate),
          userLastModifiedDate: formatDate(user.UserLastModifiedDate),
          enabled: user?.Enabled,
          status: user?.UserStatus === 'CONFIRMED' ? 'Verified' : 'Unverified',
          accountJuid: user?.Attributes.find((a) => a.Name === 'custom:juid')?.Value || '',
          customRoleKey: roleRaw, // e.g. client_admin
          customRole: toFriendlyRoleName(roleRaw),
        }
      })
    await populateFriendlyNamesForList(allUsers)
    accountStore.setCompanyUsersCache(allUsers.value)
  } catch (error) {
    console.error('Failed to load users:', error)
  } finally {
    isLoading.value = false
  }
}
const formatDatee = (isoString) => {
  return new Date(isoString).toLocaleString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const loadAllClients = async () => {
  try {
    if (accountStore.clientUsersCache && accountStore.clientUsersCache.length) {
      allCLients.value = accountStore.clientUsersCache.slice()
      await populateFriendlyNamesForList(allCLients)
      isLoading.value = false
      return
    }
    isLoading.value = true
    const data = await fetchAllClients()
    allCLients.value = data.map((admin) => {
      const roleRaw =
        admin?.Attributes.find((a) => a.Name === 'custom:role')?.Value || 'client_user'
      return {
        username: admin.Username,
        email: admin.Attributes.find((attr) => attr.Name === 'email')?.Value || 'N/A',
        userCreateDate: formatDate(admin.UserCreateDate),
        userLastModifiedDate: formatDate(admin.UserLastModifiedDate),
        enabled: admin?.Enabled,
        status: admin?.UserStatus === 'CONFIRMED' ? 'Verified' : 'Unverified',
        accountJuid: admin?.Attributes.find((a) => a.Name === 'custom:juid')?.Value || '',
        customRoleKey: roleRaw,
        customRole: toFriendlyRoleName(roleRaw),
      }
    })
    await populateFriendlyNamesForList(allCLients)
    accountStore.setClientUsersCache(allCLients.value)
  } catch (error) {
    console.error('Failed to load admins:', error)
  } finally {
    isLoading.value = false
  }
}

// Computed property to switch between users and admins
const displayedUsers = computed(() =>
  selectedRole.value === 'company' ? allUsers.value : allCLients.value
)

const totalUsers = computed(() => displayedUsers.value.length)

const roleCounts = computed(() => {
  const counts = { admin: 0, manager: 0, user: 0 }
  for (const u of displayedUsers.value) {
    const key = (u.customRoleKey || '').replace(/^client_/, '') // admin|manager|user
    if (counts[key] != null) counts[key]++
  }
  return counts
})

const filteredDisplayedUsers = computed(() => {
  const q = String(searchQuery.value ?? '')
    .trim()
    .toLowerCase()
  const roles = new Set((selectedRoles.value || []).map((r) => String(r).toLowerCase()))
  let list = displayedUsers.value
  // Filter by selected roles (checkboxes)
  list = list.filter((u) =>
    roles.has(String((u.customRoleKey || '').replace(/^client_/, '')).toLowerCase())
  )
  if (!q) return list
  return list.filter(
    (u) =>
      (u.username || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.customRole || '').toLowerCase().includes(q) ||
      (u.accountJuid || '').toLowerCase().includes(q)
  )
})

const route = useRoute()

onMounted(() => {
  VerifyAdmin()

  // Check if we're coming from user creation (via referrer or query param)
  const fromCreate =
    document.referrer.includes('/users/create-company-user') ||
    document.referrer.includes('/users/create-client-user-simple') ||
    route.query.refresh

  if (fromCreate) {
    console.log('Mounted after user creation, clearing cache and forcing refresh...')
    // Clear cache to force fresh data
    accountStore.setCompanyUsersCache([])
    accountStore.setClientUsersCache([])

    // Clear the query param from URL without triggering navigation
    if (route.query.refresh) {
      router.replace({ path: route.path, query: {} })
    }
  }

  loadUsers()
  loadAllClients()
})

// Focus search after table loads
watch(isLoading, (val, oldVal) => {
  if (oldVal && !val) {
    setTimeout(() => {
      try {
        searchRef.value?.focus?.()
      } catch {}
    }, 0)
  }
})

const headers = [
  { title: 'Username', align: 'start', key: 'username' },
  { title: 'Account', key: 'accountJuid', align: 'center' },
  { title: 'Role', key: 'customRole', align: 'center' },
  { title: 'Status', key: 'status', align: 'center' },
  { title: 'Actions', key: 'actions', align: 'start' },
]

const navigateToCreateUser = () => {
  router.push('/users/create-user')
}

function promptDelete(item) {
  console.log(item)
  userToDelete.value = item
  deleteDialog.value = true
}
async function confirmDelete() {
  console.log(userToDelete.value)
  if (!userToDelete.value) return
  try {
    await deleteUser(userToDelete.value)
    // refresh the list
    if (selectedRole.value === 'company') await loadUsers()
    else await loadAdmins()
    message.value = 'User deleted successfully.'
    snackbar.value = true
  } catch (err) {
    console.error('Failed to delete', err)
  } finally {
    deleteDialog.value = false
    userToDelete.value = null
  }
}

function promptToggle(item) {
  toggleTarget.value = { username: item.username, enabled: item.enabled }
  console.log(toggleTarget.value)
  toggleDialog.value = true
}
async function confirmToggle() {
  try {
    if (toggleTarget.value.enabled) {
      // currently enabled → deactivate
      await toggleUserStatusSetDeActiveNow(toggleTarget.value.username)
      message.value = 'User Deactivate Successfully'
      snackbar.value = true
    } else {
      // currently disabled → activate
      await toggleUserStatusSetActiveNow(toggleTarget.value.username)
      message.value = 'User Activate Successfully'
      snackbar.value = true
    }
    // refresh list
    await (selectedRole.value === 'company' ? loadUsers() : loadAllClients())
  } catch (e) {
    console.error(e)
    message.value = 'Unable to Toggle User'
    snackbar.value = true
  } finally {
    toggleDialog.value = false
  }
}

async function confirmToggleClient() {
  try {
    if (toggleTarget.value.enabled) {
      // currently enabled → deactivate
      await toggleUserStatusSetDeActiveNowClient(toggleTarget.value.username)
      const clientIndex = allCLients.value.findIndex(
        (u) => u.username === toggleTarget.value.username
      )
      if (clientIndex !== -1) {
        allCLients.value[clientIndex].enabled = !toggleTarget.value.enabled
        // Update cache
        accountStore.setClientUsersCache(allCLients.value)
      }

      message.value = 'User Deactivate Successfully'
      snackbar.value = true
    } else {
      // currently disabled → activate
      await toggleUserStatusSetActiveNowClient(toggleTarget.value.username)
      const clientIndex = allCLients.value.findIndex(
        (u) => u.username === toggleTarget.value.username
      )
      if (clientIndex !== -1) {
        allCLients.value[clientIndex].enabled = !toggleTarget.value.enabled
        // Update cache
        accountStore.setClientUsersCache(allCLients.value)
      }
      message.value = 'User Activate Successfully'
      snackbar.value = true
    }
    // refresh list
    await (selectedRole.value === 'clients' ? loadAllClients() : loadUsers())
  } catch (e) {
    console.error(e)
    message.value = 'Unable to Toggle User'
    snackbar.value = true
  } finally {
    toggleDialog.value = false
  }
}

async function confirmDeleteClients() {
  console.log(userToDelete.value)
  // if (!userToDelete.value) return
  try {
    await deleteUserClients(userToDelete.value)
    // refresh the list
    if (selectedRole.value === 'clients') await loadAllClients()
    // invalidate cache
    accountStore.setClientUsersCache([])
    // Optimistically remove from current table without full reload
    allCLients.value = allCLients.value.filter(
      (u) => String(u.username) !== String(userToDelete.value)
    )
    accountStore.setClientUsersCache(allCLients.value)
    message.value = 'User deleted successfully.'
    snackbar.value = true
  } catch (err) {
    console.error('Failed to delete', err)
  } finally {
    deleteDialog.value = false
    userToDelete.value = null
  }
}

// Resend verification email
const resendingVerification = ref(false)

async function resendVerification(username) {
  if (resendingVerification.value) return

  try {
    resendingVerification.value = true
    // Company admins use a different endpoint to manage client users
    await resendVerificationEmailClientByCompany(username)
    message.value = 'Verification email sent successfully!'
    snackbar.value = true
  } catch (error) {
    console.error('Failed to resend verification email:', error)
    message.value =
      error?.response?.data?.message || 'Failed to send verification email. Please try again.'
    snackbar.value = true
  } finally {
    resendingVerification.value = false
  }
}

const allGroups = ['client_user', 'client_manager', 'client_admin']

// Items for the role picker with friendly titles but raw values
const availableGroupOptions = computed(() =>
  allGroups.map((v) => ({ title: toFriendlyRoleName(v), value: v }))
)

// Filter functions
function applyFilters() {
  selectedRoles.value = [...tempSelectedRoles.value]
  filterMenuOpen.value = false
}

function checkAllFilters() {
  tempSelectedRoles.value = ['user', 'manager', 'admin']
}

// Navigate to create user page
function goToCreateUser() {
  router.push('/users/create-client-user-simple')
}
</script>

<style scoped>
/* Add any custom styling here */
:deep(.v-data-table tbody tr:hover) {
  background-color: #f0f0f0 !important;
}

.v-data-table {
  max-width: 100%;
  width: 100%;
  overflow-x: auto;
  /* min-width: 700px; */
}

/* Add this to your style section */
:deep(.v-data-table .inactive-row) {
  background-color: rgba(0, 0, 0, 0.774) !important;
}

:deep(.v-data-table .inactive-row > td) {
  color: #999 !important;
  opacity: 0.8;
}

:deep(.v-data-table .inactive-row:hover > td) {
  background-color: rgba(15, 4, 4, 0.887) !important;
}

/* Optional: Add "Inactive" indicator for mobile view */
:deep(.v-data-table .inactive-row .v-data-table__mobile-row:first-child::after) {
  content: ' (Inactive)';
  color: #f44336;
  font-size: 0.8em;
}

:deep(.v-data-table-header) {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: rgb(137, 44, 44) !important;
  /* Match your theme */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

:deep(.striped-rows .v-data-table__tr:nth-child(even)) {
  background-color: #f8f9fa;
  /* Very light gray - adjust to match your theme */
}

/* Hover effect */
:deep(.striped-rows .v-data-table__tr:hover) {
  background-color: #e9ecef !important;
  /* Slightly darker on hover */
}

/* Ensure text remains readable on striped rows */
:deep(.striped-rows .v-data-table__tr) {
  color: inherit;
  /* Inherits from parent */
}

:deep(.v-data-table-header) {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: rgb(137, 44, 44) !important;
  /* Match your theme */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.even-cols :deep(.v-data-table__wrapper table) {
  table-layout: fixed;
}

.even-cols :deep(th),
.even-cols :deep(td) {
  width: calc(100% / var(--even-col-count, 5));
}
</style>
